import { useState, useMemo } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  List,
  Breadcrumbs,
  Link,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useTheme } from "@mui/material/styles";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import SidebarMenu from "../components/SidebarMenu";
import { useAuth } from "../context/AuthContext";
import DashboardFooter from "../components/DashboardFooter";

const drawerWidth = 240;
const appBarHeight = 64;

/* ============================
   AGENT MENU CONFIG
============================ */
const AGENT_MENU_ITEMS = [
  { label: "Dashboard", to: "/agent/dashboard", icon: "dashboard" },
{ label: "Referred Students", to: "/agent/students", icon: "users" },
{ label: "Payouts", to: "/agent/finance", icon: "payouts" },
{ label: "Settings", to: "/agent/settings", icon: "settings" },
{ label: "Logout", to: "/logout", icon: "logout" },

];

/* ============================
   BREADCRUMB LABEL MAP
============================ */
const LABEL_MAP = {
  dashboard: "Dashboard",
  students: "Referred Students",
  settings: "Settings",
};

export default function AgentLayout() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const displayName = user?.full_name || "Agent";
  const toggleDrawer = () => setMobileOpen(!mobileOpen);

  /* ============================
     ACTIVE MENU RESOLVER
  ============================ */
  const resolveActivePath = (pathname, items) => {
    let bestMatch = null;
    let bestLength = 0;

    const checkItem = (item) => {
      if (item.to && pathname === item.to) {
        bestMatch = item.to;
        bestLength = item.to.length;
      }

      if (
        item.to &&
        pathname.startsWith(item.to + "/") &&
        item.to.length > bestLength
      ) {
        bestMatch = item.to;
        bestLength = item.to.length;
      }

      if (item.children) item.children.forEach(checkItem);
    };

    items.forEach(checkItem);
    return bestMatch || "/agent/dashboard";
  };

  const activePath = useMemo(
    () => resolveActivePath(location.pathname, AGENT_MENU_ITEMS),
    [location.pathname]
  );

  /* ============================
     BREADCRUMBS
  ============================ */
  const breadcrumbs = useMemo(() => {
    const parts = location.pathname
      .replace("/agent", "")
      .split("/")
      .filter(Boolean);

    const crumbs = [{ label: "Agent", to: "/agent/dashboard" }];
    let currentPath = "/agent";

    parts.forEach((part) => {
      currentPath += `/${part}`;
      crumbs.push({
        label:
          LABEL_MAP[part] ||
          part.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        to: currentPath,
      });
    });

    return crumbs;
  }, [location.pathname]);

  /* ============================
     DRAWER CONTENT
  ============================ */
  const drawer = (
    <>
      <Box sx={{ height: appBarHeight }} />
      <Divider />
      <List sx={{ p: 1 }}>
        <SidebarMenu
          items={AGENT_MENU_ITEMS}
          activePath={activePath}
          onNavigate={(to) => {
            if (to === "/logout") {
              logout();
              return;
            }
            navigate(to);
            setMobileOpen(false);
          }}
        />
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* ================= TOP BAR ================= */}
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          height: appBarHeight,
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: appBarHeight }}>
          {!mdUp && (
            <IconButton onClick={toggleDrawer}>
              <MenuIcon />
            </IconButton>
          )}

          <Typography fontWeight={800}>
            CBT <span style={{ color: "#1976d2" }}>Master</span>{" "}
            <span style={{ fontSize: 14, color: "#6b7280" }}>
              / Agent Portal
            </span>
          </Typography>

          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <AccountCircleIcon />
            <Typography variant="body2">
              Welcome, {displayName}
            </Typography>

            <IconButton onClick={logout}>
              <ExitToAppIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ================= SIDEBAR ================= */}
      <Drawer
        variant={mdUp ? "permanent" : "temporary"}
        open={mdUp || mobileOpen}
        onClose={toggleDrawer}
        sx={{ width: drawerWidth }}
      >
        {drawer}
      </Drawer>

      {/* ================= MAIN ================= */}
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ height: appBarHeight }} />

        {/* BREADCRUMBS */}
        <Breadcrumbs sx={{ mb: 2 }}>
          {breadcrumbs.map((b, i) =>
            i === breadcrumbs.length - 1 ? (
              <Typography key={b.to} fontWeight={700}>
                {b.label}
              </Typography>
            ) : (
              <Link
                key={b.to}
                underline="hover"
                color="inherit"
                onClick={() => navigate(b.to)}
                sx={{ cursor: "pointer" }}
              >
                {b.label}
              </Link>
            )
          )}
        </Breadcrumbs>

        <Outlet />
        <DashboardFooter />
      </Box>
    </Box>
  );
}
